// Scoped-token freshness for the RPilot-MCP auth values in the bridge store.
//
// The scopedToken minted at boot (iframe URL param or plugin config) expires;
// a widget left open long enough would then fail to create sessions or list
// them. A registered refresher supplies a fresh token on demand — the parent
// bridge's refreshTokenAction in iframe deployments, or the host's
// `scopedToken` getter function in Vue-to-Vue embeddings. RPilotChat.vue
// registers whichever applies at setup.
//
// Module-level (not a composable) so non-component callers (src/api/*) can
// guard their requests — the same reasoning as setApiConfig in ./client.js.

import { useBridgeStore } from '../stores/bridge.js'

// Refresh when the JWT expires within this window, so a token that is about
// to lapse isn't captured into a freshly created backend session.
const EXPIRY_SKEW_MS = 60_000

let refresher = null
let inflight = null

export function registerTokenRefresher(fn) {
  refresher = typeof fn === 'function' ? fn : null
}

export function hasTokenRefresher() {
  return typeof refresher === 'function'
}

function tokenExpiryMs(token) {
  if (typeof token !== 'string') return null
  const payload = token.split('.')[1]
  if (!payload) return null
  try {
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null
  } catch {
    return null
  }
}

/**
 * Resolve to a usable scoped token, refreshing first when the current one is
 * missing, expiring soon, or `force` is set. Concurrent callers share one
 * refresh. Refresh failures are swallowed — the caller proceeds with the
 * stored token and surfaces its own API error, which matches the behaviour
 * deployments without a refresher have always had.
 */
export async function ensureFreshToken({ force = false } = {}) {
  const bridge = useBridgeStore()
  if (!hasTokenRefresher()) return bridge.scopedToken
  if (!force && bridge.scopedToken) {
    const expiry = tokenExpiryMs(bridge.scopedToken)
    // Opaque (non-JWT) tokens can't be checked proactively — for those, the
    // reactive 401 retry in withFreshToken is the only trigger.
    if (expiry === null || expiry - Date.now() > EXPIRY_SKEW_MS) return bridge.scopedToken
  }
  if (!inflight) {
    inflight = (async () => {
      try {
        const token = await refresher()
        if (typeof token === 'string' && token) bridge.setScopedToken(token)
      } catch { /* keep the current token */ }
      return bridge.scopedToken
    })().finally(() => { inflight = null })
  }
  return inflight
}

/**
 * Run a token-bearing request with a fresh token, retrying once on 401 after
 * a forced refresh. `request` must re-read the bridge store when invoked (not
 * capture the token beforehand) so the retry picks up the new token.
 */
export async function withFreshToken(request) {
  await ensureFreshToken()
  try {
    return await request()
  } catch (e) {
    if (e?.status !== 401 || !hasTokenRefresher()) throw e
    await ensureFreshToken({ force: true })
    return request()
  }
}
